import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const QUERY_KEY = ['SettingsReschedule']
const ENDPOINT = `${baseURL}/v2/rest/settings/chat/reschedule`

// The failure sentence of the private API lives under `errors`, except on the
// 401 of a user without access to the clinic, which uses the singular `error`.
const sentenceIn = data => {
  if (typeof data?.errors === 'string') return data.errors
  if (typeof data?.error === 'string') return data.error

  return null
}

// The clinic may have no row stored yet, in which case the GET answers with the
// defaults and nothing is created until the first save.
export const useSettingsReschedule = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const res = await fetch(ENDPOINT, { method: 'GET', headers })

      if (!res.ok) {
        throw new Error("We couldn't load the reschedule settings")
      }

      return res.json()
    },
  })

  return { settings: data, isLoading, isError }
}

export const useUpdateReschedule = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateReschedule, ...rest } = useMutation({
    mutationFn: async attributes => {
      const res = await fetch(ENDPOINT, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reschedule: attributes }),
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        // A 422 keys `errors` by attribute — in snake_case, unlike the success
        // payload — while a 400, a 404 and a 401 send a single sentence there
        // instead. The caller needs the keyed version to put the Liquid errors
        // on the template box.
        const error = new Error(
          sentenceIn(data) || "We couldn't save the reschedule settings"
        )
        error.fieldErrors =
          data.errors && typeof data.errors === 'object' ? data.errors : {}
        throw error
      }

      return data
    },
    onSuccess: data => queryClient.setQueryData(QUERY_KEY, data),
  })

  return { updateReschedule, ...rest }
}
